import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = await login(email, password);
    setSubmitting(false);
    if (result.success) {
      navigate("/");
    } else {
      setError(result.error);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-12" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="font-heading text-2xl font-medium text-[#1A221E]">Awakening Path</span>
          </div>
          <h1 className="font-heading text-3xl font-light text-[#1A221E] tracking-tight" data-testid="login-heading">
            Welcome Back
          </h1>
          <p className="mt-2 text-sm text-[#737E79]">Continue your spiritual journey</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          {error && (
            <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="email" className="text-sm text-[#1A221E]">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="mt-1 rounded-xl bg-white border-[#E3DDD2] focus:ring-[#8FA69A] focus:border-[#8FA69A]"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-sm text-[#1A221E]">Password</Label>
            <div className="relative mt-1">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                required
                className="rounded-xl bg-white border-[#E3DDD2] focus:ring-[#8FA69A] focus:border-[#8FA69A] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737E79] hover:text-[#1A221E]"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-btn"
            className="w-full py-3 bg-[#8FA69A] text-white rounded-full text-sm font-medium transition-all duration-300 hover:bg-[#2C4A3B] disabled:opacity-50"
          >
            {submitting ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-[#737E79]">
          New to the path?{" "}
          <Link to="/register" data-testid="go-to-register" className="text-[#8FA69A] font-medium hover:text-[#2C4A3B] transition-colors">
            Create Account
          </Link>
        </p>
      </div>
    </main>
  );
}
